import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../../../common/components/Loader";
import {
  convertUserData,
  formatFloatCurrency,
} from "../../../../common/helpers";
import { useCusorLoader, useEffectOnce } from "../../../../common/hooks";
import {
  storeOrigin,
  storePublicKey,
  storeUserRefrence,
} from "../../../../common/service/storage";
import { CloseCircle } from "../../../../static/Icons";
import { useAppDispatch, useAppSelector } from "../../../../store";
import {
  addInfo,
  closeModal,
  resetCurrencyInfo,
} from "../../../auth/store/slice";
import { getCustomer, getExchangeRate } from "../../../auth/store/thunk";
import { bankTransferActions } from "../../../banktransfer/store/slice";
import { datalist } from "./Icons";
import { routes } from "../../../../common/routes";
import { AppFeature } from "../../../auth/types";
import {
  fetchCategories,
  fetchOperators,
} from "../../../utilities/store/thunk";

function Dashboard() {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const data = JSON.parse(JSON.stringify(params));
  const { user, isloading } = useAppSelector((state) => state?.AppSlice);
  const country_currency = user?.asset?.country_currency;
  const checkout_logo = user?.merchant_app?.checkout_logo;
  const logo_url = user?.merchant_app?.logo_url;
  const colors = user?.merchant_app?.app_color_theme;
  const apps = user?.merchant_app.app_features
    .map((app: AppFeature) => ({
      ...app,
      ...datalist[app.name],
    }))
    .filter((app: any) => app.status === true && app?.style !== undefined);
  const [clickLoading, setClickLoading] = useState(false);
  useCusorLoader(clickLoading);
  const navigate = useNavigate();
  const selectedCountry = useAppSelector(
    (state) => state.banktransfer.countryInfo.selected
  );
  const { balance } = useAppSelector((state) => state.AppSlice.info);
  const txn_currency_info = useAppSelector(
    (state) => state.AppSlice.currency_info.data
  );
  const dispatch = useAppDispatch();

  useEffectOnce(() => {
    storePublicKey(data?.public_key);
    storeUserRefrence(data?.reference);
    storeOrigin(data?.origin.split("{")[0]);

    if (!data?.balance) {
      dispatch(closeModal());
    }

    if (!user) {
      const temp = convertUserData(data);
      const req = {
        user_reference: temp.reference,
        navigateError: () => {
          navigate(routes.onboard);
        },
      };
      dispatch(getCustomer(req));
    }
    if (selectedCountry?._id) {
      dispatch(bankTransferActions.resetBankSlice());
    }

    if (txn_currency_info) {
      dispatch(resetCurrencyInfo());
    }

    // the url caches the url in state
    dispatch(
      addInfo({
        balance: data?.balance,
        url: window.location.search,
      })
    );
  });

  const clickHandler = (path: string, title: string, catid?: string) => {
    if (!user?.asset?.country_code) return;
    setClickLoading(true);
    if (catid && title !== "Utilities") {
      const temp = routes.utility.dynamic_type.link(catid);
      return dispatch(
        fetchOperators({
          category_id: catid,
          data: { country_code: user?.asset?.country_code },
        })
      )
        .unwrap()
        .then(() => navigate(temp, { state: { name: title } }))
        .finally(() => setClickLoading(false));
    }
    if (!catid && title === "Utilities") {
      return dispatch(fetchCategories())
        .unwrap()
        .then(() => navigate(path))
        .finally(() => setClickLoading(false));
    }
    if (title === "Fund Wallet") {
      return dispatch(getExchangeRate(country_currency))
        .unwrap()
        .then(() => navigate(path))
        .finally(() => setClickLoading(false));
    }
    if (!catid && title !== "Utilities") {
      navigate(path);
      setClickLoading(false);
    }
  };

  const toggleHandler = () => {
    dispatch(closeModal());
  };

  return (
    <section className="pb-6 min-h-[40rem]">
      {isloading ? (
        <div className="min-h-[40rem] bg-white grid place-content-center">
          <Loader />
        </div>
      ) : (
        <>
          <header
            style={{
              background: colors?.background,
              color: colors?.text,
            }}
            className="text-white bg-blue-10 pt-7 pb-8 pr-7 pl-12 relative flex flex-col gap-y-4 text-base"
          >
            <h1 className="">
              <img
                src={checkout_logo ? checkout_logo : logo_url}
                alt="application logo"
                className="w-12 h-12 object-contain border border-white bg-white"
              />
            </h1>
            <h2 className=" font-bold mt-2">{`${user?.first_name} ${user?.last_name}`}</h2>
            <div className="flex items-start gap-x-18">
              {balance && (
                <div>
                  <h3 className="text-xs">Wallet Balance</h3>
                  <p className="font-bold">
                    {country_currency + " " + formatFloatCurrency(balance)}
                  </p>
                </div>
              )}
              <div>
                <h3 className="text-xs">Finswich Number</h3>
                <p className="font-bold">{user?.phone_number}</p>
              </div>
            </div>
            <button onClick={toggleHandler} className="absolute top-7 right-7">
              <CloseCircle color={colors?.text} />
            </button>
          </header>
          <section className="grid grid-cols-4 gap-3 pt-9 px-6 pb-26">
            {apps?.map(
              ({ Icon, style, title, link, catid }: any, idx: number) => (
                <button
                  key={idx}
                  onClick={() => clickHandler(link, title, catid)}
                  className={`text-xs py-3 px-2.5 h-23 grid grid-rows-[max-content_1fr_max-content] ${
                    clickLoading ? "!cursor-wait" : ""
                  } rounded-xl ${style}`}
                >
                  {Icon && (
                    <React.Suspense>
                      <Icon />
                    </React.Suspense>
                  )}
                  <span className="row-start-3 row-end-4 text-start font-medium">
                    {title}
                  </span>
                </button>
              )
            )}
          </section>
        </>
      )}
    </section>
  );
}

export default Dashboard;
